import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Save, Cancel, EditNote } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { url } from '../../constants';

const EditDeal = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get today's date in YYYY-MM-DD format to restrict the calendar
  const today = new Date().toISOString().split('T')[0];

  const [categories, setCategories] = useState([]);
  const [loadingInit, setLoadingInit] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    deal_name: '',
    description: '',
    cate_id: '',
    category_name: '',
    original_price: '',
    discounted_price: '',
    discount_percentage: '',
    expires_at: '', 
    is_active: true,
  });

  // Fetch categories and existing deal data on mount
  useEffect(() => {
    const init = async () => {
      try {
        const token = localStorage.getItem('vendor_token');
        if (!token) {
          navigate('/vendor/login');
          return;
        }

        // 1. Fetch Categories
        const catRes = await fetch(`${url}/users/get_categories`);
        if (!catRes.ok) throw new Error('Failed to load categories');
        const catData = await catRes.json();
        setCategories(catData || []);

        // 2. Safely Get Deal Data (Including is_active status)
        let dealData = location.state?.deal;

        // Fallback: If page is refreshed and state is lost, fetch from authenticated vendor route
        if (!dealData) {
          const dealRes = await fetch(`${url}/vendors/me/deals`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (!dealRes.ok) throw new Error('Failed to load deal details');
          const allVendorDeals = await dealRes.json();
          dealData = allVendorDeals.find(d => (d._id || d.id) === dealId);
          
          if (!dealData) {
            throw new Error('Deal not found or you do not have permission to edit it.');
          }
        }

        // Safely format the existing validity date to YYYY-MM-DD for the date input
        let formattedDate = '';
        if (dealData.validity || dealData.expires_at) {
          const dateObj = new Date(dealData.validity || dealData.expires_at);
          if (!isNaN(dateObj.valueOf())) {
            formattedDate = dateObj.toISOString().split('T')[0];
          }
        }

        setForm({
          deal_name: dealData.deal_name || '',
          description: dealData.description || '',
          cate_id: dealData.cate_id || '',
          category_name: dealData.category || '',
          original_price: dealData.original_price || '',
          discounted_price: dealData.discounted_price || '',
          discount_percentage: dealData.percentage_discount || dealData.discount_percentage || '',
          expires_at: formattedDate, 
          is_active: dealData.is_active !== undefined ? dealData.is_active : true, // ✅ Will now correctly read false
        });

      } catch (e) {
        console.error('Error initializing EditDeal', e);
        setError(e.message || 'Failed to initialize form');
      } finally {
        setLoadingInit(false);
      }
    };
    init();
  }, [dealId, navigate, location.state]);

  const computeDiscountPercent = (orig, disc) => {
    const o = Number(orig);
    const d = Number(disc);
    if (!o || !d || d >= o) return '';
    return Math.round(((o - d) / o) * 100);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;

    if (field === 'original_price' || field === 'discounted_price') {
      const updated = { ...form, [field]: value };
      const percent = computeDiscountPercent(
        updated.original_price,
        updated.discounted_price
      );
      setForm({ ...updated, discount_percentage: percent });
      return;
    }

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCategorySelect = (e) => {
    const cateId = Number(e.target.value);
    const cat = categories.find((c) => c.cate_id === cateId);
    setForm((prev) => ({
      ...prev,
      cate_id: cateId,
      category_name: cat ? cat.cate_name : '',
    }));
  };

  const handleToggleActive = (e) => {
    setForm((prev) => ({
      ...prev,
      is_active: e.target.checked,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('vendor_token');
      if (!token) throw new Error('Vendor session expired. Please login again.');

      const body = {
        deal_name: form.deal_name || undefined,
        description: form.description || undefined,
        category: form.category_name || undefined,
        cate_id: form.cate_id !== '' ? Number(form.cate_id) : undefined,
        original_price:
          form.original_price !== '' ? Number(form.original_price) : undefined,
        discounted_price:
          form.discounted_price !== '' ? Number(form.discounted_price) : undefined,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : undefined,
        is_active: form.is_active,
      };

      const res = await fetch(`${url}/vendors/me/deals/${dealId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update deal');

      setSuccess('Deal updated successfully!');
      setTimeout(() => {
        navigate('/vendor/deals/view');
      }, 800);
    } catch (err) {
      setError(err.message || 'Failed to update deal');
    } finally {
      setSaving(false);
    }
  };

  if (loadingInit) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 6 }}>
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#1a7f3a' }} />
            <Typography sx={{ mt: 2 }}>Loading deal details...</Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: isMobile ? 2 : 6 }}>
      <Container maxWidth="sm" sx={{ px: isMobile ? 1.5 : 0 }}>
        <Paper
          elevation={3}
          sx={{
            p: isMobile ? 2 : 3,
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            sx={{ mb: isMobile ? 1.5 : 2 }}
          >
            <EditNote sx={{ color: '#1a7f3a', fontSize: isMobile ? 28 : 32 }} />
            <Box>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700}>
                Edit Deal
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', maxWidth: 260 }}
              >
                Update the details of your existing deal
              </Typography>
            </Box>
          </Stack>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 1.5 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{ mb: 1.5 }}
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={isMobile ? 1.5 : 2}>
              <TextField
                label="Deal name"
                value={form.deal_name}
                onChange={handleChange('deal_name')}
                fullWidth
                required
                size={isMobile ? 'small' : 'medium'}
              />

              <TextField
                label="Description"
                value={form.description}
                onChange={handleChange('description')}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                multiline
                minRows={3}
              />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={isMobile ? 1.5 : 2}
              >
                <TextField
                  select
                  label="Category"
                  value={form.cate_id || ''}
                  onChange={handleCategorySelect}
                  fullWidth
                  required
                  size={isMobile ? 'small' : 'medium'}
                  helperText="Select the category for this deal"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.cate_id} value={cat.cate_id}>
                      {cat.cate_name}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Valid Upto Date Picker */}
                <TextField
                  fullWidth
                  label="Valid Upto"
                  type="date"
                  value={form.expires_at}
                  onChange={handleChange('expires_at')}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  size={isMobile ? 'small' : 'medium'}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: today,
                    style: { cursor: 'pointer' } 
                  }}
                  helperText="Select expiry date"
                />
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={isMobile ? 1.5 : 2}
              >
                <TextField
                  label="Original price"
                  type="number"
                  value={form.original_price}
                  onChange={handleChange('original_price')}
                  fullWidth
                  required
                  size={isMobile ? 'small' : 'medium'}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label="Discounted price"
                  type="number"
                  value={form.discounted_price}
                  onChange={handleChange('discounted_price')}
                  fullWidth
                  required
                  size={isMobile ? 'small' : 'medium'}
                  inputProps={{ min: 0 }}
                />
              </Stack>

              <TextField
                label="Discount percentage"
                type="number"
                value={form.discount_percentage ?? ''}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                InputProps={{ readOnly: true }}
                helperText="Auto-calculated from prices"
              />

              {/* Toggle for Active Status */}
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={handleToggleActive}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#1a7f3a',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#1a7f3a',
                      },
                    }}
                  />
                }
                label={form.is_active ? "Deal is Active" : "Deal is Paused"}
                sx={{ ml: 0.5 }}
              />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={isMobile ? 1 : 2}
                sx={{ mt: isMobile ? 1 : 2 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={saving}
                  fullWidth
                  sx={{
                    bgcolor: '#1a7f3a',
                    textTransform: 'none',
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    fontWeight: 700,
                    py: isMobile ? 0.8 : 1.2,
                    '&:hover': { bgcolor: '#0f5328' },
                  }}
                >
                  {saving ? 'Updating...' : 'Update Deal'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  fullWidth
                  sx={{
                    color: '#4B5563',
                    borderColor: '#D1D5DB',
                    textTransform: 'none',
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    fontWeight: 600,
                    py: isMobile ? 0.8 : 1.2,
                    '&:hover': { bgcolor: '#F3F4F6', borderColor: '#9CA3AF' },
                  }}
                  onClick={() => navigate('/vendor/deals/view')}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default EditDeal;